import React from 'react';
import addedIcon from '../resources/images/ico/added.svg';
import deletedIcon from '../resources/images/ico/deleted.svg';
import updatedIcon from '../resources/images/ico/updated.svg';
import defaultPopIcon from '../resources/images/ico/icoPop.png';
import { excelMultiSelectFilterFn } from './excelColumnFilter';
import { theme } from 'styles/theme';

/** img 컬럼 기본 아이콘 (미지정 시 사용) */
const defaultImgIcon = defaultPopIcon;

/** 좌측 시스템 컬럼 — 데이터/내보내기·키보드 편집 대상 아님 */
export const HIGRID_SYSTEM_COLUMN_IDS = ['mod', 'rowCheck', 'rowNumber'];

export function isHiGridSystemColumnId(id) {
  return HIGRID_SYSTEM_COLUMN_IDS.includes(id);
}

/**
 * 날짜 값에서 연/월/일 추출 (8자리 YYYYMMDD, yyyy-MM-dd, yyyy/MM/dd, Date 지원)
 */
function parseDateParts(value) {
  if (value == null || value === '') return null;
  let str = '';
  if (typeof value === 'string') str = value.trim();
  else if (value instanceof Date) str = value.toISOString().slice(0, 10);
  else str = String(value).trim();
  if (/^\d{8}$/.test(str)) {
    return { y: str.slice(0, 4), m: str.slice(4, 6), d: str.slice(6, 8) };
  }
  const withSep = str.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (withSep) {
    return { y: withSep[1], m: withSep[2].padStart(2, '0'), d: withSep[3].padStart(2, '0') };
  }
  return null;
}
 
/**
 * 날짜 비교용: 값을 yyyy-MM-dd 문자열로 통일 (20260114 ↔ 2026-01-14 동일 처리)
 */
export function normalizeDateForCompare(value) {
  const parts = parseDateParts(value);
  if (!parts) return value == null || value === '' ? '' : String(value).trim().slice(0, 10);
  return `${parts.y}-${parts.m}-${parts.d}`;
}

/**
 * 날짜 값을 meta.format 기준 표시 문자열로 변환
 * - format 'yyyy-MM-dd' → 2026-01-14, 'yyyy/MM/dd' → 2026/01/14, 'yyyyMMdd' → 20260114
 */
function formatDateValue(value, format) {
  const fmt = format || 'yyyy-MM-dd';
  const parts = parseDateParts(value);
  if (!parts) return value != null ? String(value).trim().slice(0, 10) : '';
  if (fmt === 'yyyyMMdd') return `${parts.y}${parts.m}${parts.d}`;
  return fmt.replace('yyyy', parts.y).replace('MM', parts.m).replace('dd', parts.d);
}

/**
 * 셀 표시값 계산 (숫자 천단위 포맷, 날짜 format, combo value→label 등)
 */
function formatCellValue(value, meta) {
  if (meta?.type === 'number' && value != null && value !== '') {
    return Number(value).toLocaleString();
  }
  if (meta?.type === 'date') {
    return formatDateValue(value, meta?.format);
  }
  if (meta?.type === 'combo' && Array.isArray(meta?.options)) {
    const opt = meta.options.find(
      (o) => (o?.value ?? o) === value || String(o?.value ?? o) === String(value)
    );
    return opt != null ? (opt.label ?? opt.value ?? String(value)) : (value != null ? String(value) : '');
  }
  return value != null ? String(value) : '';
}

/**
 * TanStack 테이블용 전체 컬럼 생성 (mod + 행체크 + 행번호 + 데이터 컬럼)
 * @param {Array} dataColumns - TanStack 형식 컬럼 정의 (id, accessorKey, header, size, meta)
 * @param {boolean} showRowNumber - No. 컬럼 표시 여부
 * @param {Object} [treeOptions] - 트리 모드 시 meta.treeColumn 컬럼은 트리 셀(들여쓰기+펼침+값) 렌더
 * @param {number} [rowNumberSize=40] - No. 컬럼 폭(px)
 * @param {boolean} [showModColumn=true] - A/U/D 상태(펜) 컬럼 표시 여부
 * @param {boolean} [enableRowCheck=false] - 행 멀티체크 컬럼 (mod와 No. 사이)
 * @returns {Array} useReactTable에 넘길 columns
 */
export function buildTableColumns(
  dataColumns = [],
  showRowNumber = true,
  treeOptions = null,
  rowNumberSize = 40,
  showModColumn = true,
  enableRowCheck = false
) {
  const base = [];
  // eslint-disable-next-line no-unused-vars
  const isTree = Boolean(treeOptions);

  // 1) mod — CRUD 상태 (A/U/D)
  if (showModColumn) {
    base.push({
      id: 'mod',
      accessorKey: 'mod',
      header: '-',
      size: 24,
      minSize: 24,
      maxSize: 24,
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ getValue }) => {
        const v = getValue();
        const src = v === 'A' ? addedIcon : v === 'D' ? deletedIcon : v === 'U' ? updatedIcon : null;
        if (!src) return null;
        return React.createElement('img', {
          src,
          alt: v,
          style: { height: 14, width: 14, display: 'block', margin: '0 auto' },
        });
      },
      meta: { editable: false, align: 'center', systemColumn: true },
    });
  }

  // 2) 행 체크 — 멀티 삭제/저장/승인 등 (데이터 필드 아님, meta로 토글)
  if (enableRowCheck) {
    base.push({
      id: 'rowCheck',
      header: ({ table }) => {
        const rows = table.getRowModel().rows || [];
        const allChecked =
          rows.length > 0 && rows.every((r) => table.options.meta?.isRowChecked?.(r.id));
        const someChecked =
          !allChecked && rows.some((r) => table.options.meta?.isRowChecked?.(r.id));
        return React.createElement('input', {
          type: 'checkbox',
          checked: allChecked,
          ref: (el) => {
            if (el) el.indeterminate = someChecked;
          },
          title: '전체 선택/해제',
          'aria-label': '전체 선택/해제',
          onMouseDown: (ev) => ev.stopPropagation(),
          onClick: (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            table.options.meta?.checkAllVisibleRows?.(!allChecked);
          },
          style: { cursor: 'pointer', margin: 0 },
        });
      },
      size: 44,
      minSize: 44,
      maxSize: 44,
      enableSorting: false,
      enableColumnFilter: false,
      enableResizing: false,
      cell: ({ row, table }) => {
        const checked = !!table.options.meta?.isRowChecked?.(row.id);
        return React.createElement('input', {
          type: 'checkbox',
          checked,
          title: '행 선택',
          'aria-label': '행 선택',
          onMouseDown: (ev) => ev.stopPropagation(),
          onClick: (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            table.options.meta?.toggleRowCheck?.(row.id);
          },
          style: { cursor: 'pointer', margin: 0, display: 'block', marginLeft: 'auto', marginRight: 'auto' },
        });
      },
      meta: { editable: false, align: 'center', systemColumn: true, rowCheck: true },
    });
  }

  // 3) No. — 화면 표시 순서 (리사이즈·필터 비활성 — "No." 헤더 잘림 방지)
  if (showRowNumber) {
    const noSize = Math.max(56, Number(rowNumberSize) || 56);
    base.push({
      id: 'rowNumber',
      header: 'No.',
      size: noSize,
      minSize: noSize,
      maxSize: noSize,
      enableSorting: false,
      enableColumnFilter: false,
      enableResizing: false,
      cell: ({ row, table }) => {
        const rows = table.getRowModel().rows;
        let idx = rows.findIndex((r) => r.original === row.original);
        if (idx < 0) idx = rows.indexOf(row);
        if (idx < 0) idx = rows.findIndex((r) => r.id === row.id);
        return idx >= 0 ? idx + 1 : '';
      },
      meta: { editable: false, align: 'center', systemColumn: true },
    });
  }

  dataColumns.forEach((col) => {
    // meta.visible === false 또는 col.visible === false 이면 컬럼 비표시 (Real Grid visible 호환)
    if (col.visible === false || col.meta?.visible === false) return;
    const field = col.accessorKey || col.id;
    if (!field) return;
    const footerOpt = col.footer ?? col.meta?.footer;
    const meta = { ...(col.meta ?? {}), ...(footerOpt != null && { footer: footerOpt }) };
    const columnDef = {
      id: col.id ?? field,
      accessorKey: field,
      header: col.meta?.required ? `*${col.header ?? field}` : col.header ?? field,
      size: col.size ?? 150,
      minSize: col.minSize ?? 50,
      maxSize: col.maxSize,
      enableSorting: col.enableSorting === false || col.sortable === false ? false : true,
      enableColumnFilter: col.enableColumnFilter === false || col.filterable === false ? false : true,
      filterFn: col.filterFn || excelMultiSelectFilterFn,
      meta,
      ...(footerOpt && {
        footer: ({ table, column }) => {
          const opt = column.columnDef.meta?.footer;
          if (!opt || !table) return '';
          const rows = table.getRowModel().rows;
          const data = rows.map((r) => r.original);
          const key = column.columnDef.accessorKey;
          /** template 있으면 '레이블: {0}' 형태로 표시, 없으면 계산값만 */
          const applyTemplate = (formatted, template) => {
            if (template != null && String(template).includes('{0}')) {
              return String(template).replace('{0}', formatted);
            }
            return formatted;
          };
          if (opt.option === 'text') return opt.text ?? '';
          if (opt.option === 'sum') {
            const sum = data.reduce((acc, row) => acc + (Number(row[key]) || 0), 0);
            const formatted = typeof sum === 'number' ? sum.toLocaleString() : String(sum);
            return applyTemplate(formatted, opt.template);
          }
          if (opt.option === 'count') {
            const n = data.filter((r) => r[key] != null && r[key] !== '').length;
            return applyTemplate(String(n), opt.template);
          }
          // min, max, avg: 숫자만 추출 후 계산
          const nums = data.map((r) => Number(r[key])).filter((n) => !Number.isNaN(n));
          if (opt.option === 'min') {
            if (nums.length === 0) return '';
            return applyTemplate(Math.min(...nums).toLocaleString(), opt.template);
          }
          if (opt.option === 'max') {
            if (nums.length === 0) return '';
            return applyTemplate(Math.max(...nums).toLocaleString(), opt.template);
          }
          if (opt.option === 'avg') {
            if (nums.length === 0) return '';
            const avg = nums.reduce((a, b) => a + b, 0) / nums.length;
            const decimals = opt.decimals != null ? opt.decimals : 2;
            const formatted = decimals === 0 ? Math.round(avg).toLocaleString() : avg.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
            return applyTemplate(formatted, opt.template);
          }
          // sumFields: 여러 컬럼 합계. template 있으면 '금액1 + 금액2 = {0}' 형태로 표시
          if (opt.option === 'sumFields' && Array.isArray(opt.fields) && opt.fields.length > 0) {
            let total = 0;
            for (const f of opt.fields) {
              total += data.reduce((acc, row) => acc + (Number(row[f]) || 0), 0);
            }
            const formatted = typeof total === 'number' ? total.toLocaleString() : String(total);
            return applyTemplate(formatted, opt.template);
          }
          // avgFields: 여러 컬럼 합계의 평균 (행별 금액1+금액2 합한 뒤 그 평균). template: '금액1,금액2 평균 = {0}'
          if (opt.option === 'avgFields' && Array.isArray(opt.fields) && opt.fields.length > 0 && data.length > 0) {
            let total = 0;
            for (const f of opt.fields) {
              total += data.reduce((acc, row) => acc + (Number(row[f]) || 0), 0);
            }
            const avg = total / data.length;
            const decimals = opt.decimals != null ? opt.decimals : 2;
            const formatted = decimals === 0 ? Math.round(avg).toLocaleString() : avg.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
            return applyTemplate(formatted, opt.template);
          }
          return '';
        },
      }),
      cell: ({ getValue, row, column, table }) => {
        const value = getValue();
        const meta = column.columnDef.meta || {};
        // 트리 컬럼: 들여쓰기 + [고정폭 화살표 영역] + [체크박스(옵션)] + 텍스트 (같은 레벨에서 들여쓰기 정렬 맞춤)
        if (table.options.meta?.treeOptions && meta?.treeColumn) {
          const depth = row.depth ?? 0;
          const canExpand = row.getCanExpand?.();
          const isExpanded = row.getIsExpanded?.();
          const treeOpts = table.options.meta.treeOptions;
          const checkboxField = treeOpts?.checkboxField;
          const trueVal = treeOpts?.checkboxTrueValue ?? 'Y';
          const falseVal = treeOpts?.checkboxFalseValue ?? 'N';
          const cascadeSave = table.options.meta?.directSaveTreeCheckboxCascade;
          /** 화살표 유무와 관계없이 동일 너비로 두어 같은 depth에서 체크박스/텍스트 시작 위치 일치 */
          const expandAreaWidth = 20;
          const children = [
            canExpand
              ? React.createElement(
                  'span',
                  { key: 'expand', style: { width: expandAreaWidth, minWidth: expandAreaWidth, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } },
                  React.createElement('button', {
                    type: 'button',
                    onClick: (e) => { e.stopPropagation(); row.toggleExpanded?.(); },
                    style: { padding: 0, border: 'none', background: 'none', cursor: 'pointer', lineHeight: 1 },
                    'aria-label': isExpanded ? '접기' : '펼치기',
                  }, React.createElement('span', {
                    style: { display: 'inline-block', transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)', fontSize: 10 },
                  }, '▼'))
                )
              : React.createElement('span', { key: 'expand', style: { width: expandAreaWidth, minWidth: expandAreaWidth, display: 'inline-block', flexShrink: 0 } }),
          ];
          if (checkboxField && typeof cascadeSave === 'function') {
            const checked = row.original[checkboxField] === trueVal;
            const newVal = checked ? falseVal : trueVal;
            children.push(
              React.createElement('input', {
                key: `tree-cb-${row.id}-${String(row.original[checkboxField])}`,
                type: 'checkbox',
                checked,
                readOnly: true,
                tabIndex: -1,
                style: { cursor: 'pointer', margin: 0, flexShrink: 0 },
                onMouseDown: (ev) => ev.stopPropagation(),
                onClick: (ev) => {
                  ev.preventDefault();
                  ev.stopPropagation();
                  cascadeSave(row.id, checkboxField, newVal);
                },
              })
            );
          }
          // 트리 컬럼도 편집 가능한 경우 편집 모드일 때 편집기 표시
          const isEditing = table.options.meta?.editingCell?.rowId === row.id && table.options.meta?.editingCell?.field === field;
          let isEditable = typeof meta.editable === 'function' ? meta.editable(row.original) : Boolean(meta.editable);
          const colOpts = table.options.meta?.colOptions;
          if (colOpts?.[field]?.readOnly !== undefined) {
            const ro = colOpts[field].readOnly;
            const effectiveReadOnly = typeof ro === 'function' ? ro(row.original) : ro;
            isEditable = isEditable && !effectiveReadOnly;
          }
          if (isEditable && isEditing) {
            const display = formatCellValue(value, meta);
            const editorContent = table.options.meta?.renderCellEditor?.({ row, column, value, meta }) ?? display;
            children.push(React.createElement('span', { key: 'val', style: { flex: 1, minWidth: 0 } }, editorContent));
          } else {
            children.push(React.createElement('span', { key: 'val', style: { flex: 1, minWidth: 0 } }, value != null ? String(value) : ''));
          }
          return React.createElement(
            'div',
            { style: { display: 'flex', alignItems: 'center', gap: 4, paddingLeft: depth * 16 } },
            ...children
          );
        }
        // 2단 스택 숫자 셀 (월별예산 등) — 상/하 각각 독립 포커스·편집
        if (meta.stackPair) {
          const sp = meta.stackPair;
          const topField = sp.top;
          const bottomField = sp.bottom;
          const topVal = row.original?.[topField];
          const bottomVal = row.original?.[bottomField];
          const editing = table.options.meta?.editingCell;
          const stackFocus = table.options.meta?.stackFocus;
          const isEditTop = editing?.rowId === row.id && editing?.field === topField;
          const isEditBottom = editing?.rowId === row.id && editing?.field === bottomField;
          const isFocusTop =
            (stackFocus?.rowId === row.id && stackFocus?.field === topField) || isEditTop;
          const isFocusBottom =
            (stackFocus?.rowId === row.id && stackFocus?.field === bottomField) || isEditBottom;
          const lineColor = theme.colors.grid.stackDivider;
          const focusColor = theme.colors.primary;
          const halfStyle = (isLast, isHalfFocused) => ({
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            padding: '1px 4px',
            minHeight: 0,
            borderBottom: isLast ? 'none' : `1px solid ${lineColor}`,
            boxSizing: 'border-box',
            width: '100%',
            position: 'relative',
            zIndex: isHalfFocused ? 2 : 0,
            boxShadow: isHalfFocused ? `inset 0 0 0 2px ${focusColor}` : undefined,
            background: isHalfFocused ? 'rgba(37, 99, 235, 0.06)' : undefined,
          });
          const renderHalf = (which) => {
            const f = which === 'top' ? topField : bottomField;
            const v = which === 'top' ? topVal : bottomVal;
            const isEd = which === 'top' ? isEditTop : isEditBottom;
            if (isEd) {
              const editor = table.options.meta?.renderCellEditor?.({
                row,
                column: {
                  ...column,
                  id: f,
                  columnDef: {
                    ...column.columnDef,
                    id: f,
                    accessorKey: f,
                    meta: { ...meta, type: 'number', editable: true, stackPair: undefined },
                  },
                },
                value: v,
                meta: { ...meta, type: 'number', editable: true, stackPair: undefined },
              });
              return editor ?? formatCellValue(v, { type: 'number' });
            }
            return formatCellValue(v, { type: 'number' });
          };
          return React.createElement(
            'div',
            {
              style: {
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                height: '100%',
                minHeight: '100%',
                boxSizing: 'border-box',
              },
            },
            React.createElement('div', { style: halfStyle(false, isFocusTop) }, renderHalf('top')),
            React.createElement('div', { style: halfStyle(true, isFocusBottom) }, renderHalf('bottom'))
          );
        }
        const isEditing = table.options.meta?.editingCell?.rowId === row.id && table.options.meta?.editingCell?.field === field;
        let isEditable = typeof meta.editable === 'function' ? !!meta.editable(row.original) : Boolean(meta.editable);
        const colOpts = table.options.meta?.colOptions;
        if (colOpts?.[field]?.readOnly !== undefined) {
          const ro = colOpts[field].readOnly;
          const effectiveReadOnly = typeof ro === 'function' ? ro(row.original) : ro;
          isEditable = isEditable && !effectiveReadOnly;
        }
        if (isEditable && isEditing) {
          const display = formatCellValue(value, meta);
          return table.options.meta?.renderCellEditor?.({ row, column, value, meta }) ?? display;
        }
        // 체크박스 컬럼: 편집 가능 시에만 클릭 토글 (editable=false면 비활성)
        // value를 key에 넣어 값 변경 시 즉시 반영되도록 강제 리렌더
        if (meta.type === 'checkbox') {
          const trueVal = meta.trueValue ?? 'Y';
          const falseVal = meta.falseValue ?? 'N';
          const checked = value === trueVal;
          const newVal = checked ? falseVal : trueVal;
          return React.createElement('input', {
            key: `cb-${row.id}-${field}-${String(value)}-${isEditable ? '1' : '0'}`,
            type: 'checkbox',
            checked,
            disabled: !isEditable,
            readOnly: true,
            tabIndex: -1,
            title: !isEditable
              ? meta.title || '수정할 수 없습니다.'
              : meta.title || undefined,
            style: {
              cursor: isEditable ? 'pointer' : 'not-allowed',
              margin: 0,
              opacity: isEditable ? 1 : 0.45,
            },
            onMouseDown: (ev) => ev.stopPropagation(),
            onClick: (ev) => {
              ev.preventDefault();
              ev.stopPropagation();
              if (!isEditable) return;
              table.options.meta?.directSaveCell?.(row.id, field, newVal, {
                rowData: row.original,
                colMeta: meta,
              });
            },
          });
        }
        // 이미지/아이콘 컬럼: src 있으면 img, 없으면 기본 아이콘(icoPop.png)
        if (meta.type === 'img') {
          const imgSrc = meta.imgSrc || defaultImgIcon;
          const imgAlt = meta.imgAlt ?? '선택';
          return React.createElement('img', {
            src: imgSrc,
            alt: imgAlt,
            style: { height: 14, display: 'block', margin: '0 auto', cursor: 'pointer' },
            role: 'button',
          });
        }
        const display = formatCellValue(value, meta);
        // 코드조회(showSearch): 표시 모드에서도 셀 안쪽 ··· 버튼 (FormArea 와 동일 UX)
        if (meta.showSearch && typeof meta.onSearch === 'function') {
          const searchBtnTitle = meta.searchTitle || '코드 조회';
          // 외 N건 등: row.deptTitle / projectTitle (field deptDisp → deptTitle)
          const tipField =
            meta.hoverTitleField ||
            (String(field).endsWith('Disp')
              ? `${String(field).slice(0, -4)}Title`
              : `${field}Title`);
          const hoverTitle = row.original?.[tipField] || meta.title || undefined;
          return React.createElement(
            'div',
            {
              title: hoverTitle,
              style: {
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                height: '100%',
                minWidth: 0,
                paddingRight: 22,
                boxSizing: 'border-box',
              },
            },
            React.createElement(
              'span',
              {
                style: {
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              },
              display
            ),
            React.createElement(
              'button',
              {
                type: 'button',
                title: searchBtnTitle,
                'aria-label': searchBtnTitle,
                onMouseDown: (ev) => {
                  ev.preventDefault();
                  ev.stopPropagation();
                },
                onClick: (ev) => {
                  ev.preventDefault();
                  ev.stopPropagation();
                  meta.onSearch({
                    name: field,
                    field,
                    value,
                    row: row.original,
                    rowId: row.id,
                  });
                },
                style: {
                  position: 'absolute',
                  top: '50%',
                  right: 2,
                  transform: 'translateY(-50%)',
                  width: 20,
                  height: 20,
                  margin: 0,
                  padding: 0,
                  border: 'none',
                  borderRadius: 2,
                  background: 'transparent',
                  color: theme.colors.text.secondary,
                  fontSize: 13,
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                  lineHeight: 1,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1,
                },
              },
              '\u00B7\u00B7\u00B7'
            )
          );
        }
        // 읽기 전용 외 N건 등 — row.*Title 호버
        if (String(field).endsWith('Disp') && row.original?.[`${String(field).slice(0, -4)}Title`]) {
          return React.createElement(
            'span',
            {
              title: row.original[`${String(field).slice(0, -4)}Title`],
              style: {
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            },
            display
          );
        }
        return display;
      },
    };
    base.push(columnDef);
  });

  return base;
}
